import { fetchCallStatsByType, fetchExtensionCDR } from '@/services/api';
import type { CallRecord, CallStats, Extension, ExtensionReportData, MonthlyCallData } from '@/types';
import { formatHMS } from '@/components/reporting/extensionPdfs';
import { statMissed } from './extensionActivity';

/** Data for the Call Summary and Call Detail reports, for the Reports page and scheduled reports. */

interface MonthInfo {
  year: number;
  month: number;
  key: string;
  label: string;
}

const pad = (value: number) => String(value).padStart(2, '0');

/** "2026/10/01 00:00:00", the PBX's date format. */
function yeastarDateTime(date: Date, endOfDay: boolean) {
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${endOfDay ? '23:59:59' : '00:00:00'}`;
}

/** A local date from "2026-10-01". */
function parseDay(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function monthInfo(date: Date): MonthInfo {
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    key: `${date.getFullYear()}-${pad(date.getMonth() + 1)}`,
    label: date.toLocaleString('en-GB', { month: 'short', year: 'numeric' }),
  };
}

/** Inbound and outbound statistics of one extension, for one month or part of it. */
async function monthStats(extension: Extension, info: MonthInfo, from?: Date, to?: Date): Promise<MonthlyCallData> {
  const first = new Date(info.year, info.month, 1);
  const last = new Date(info.year, info.month + 1, 0);
  const start = yeastarDateTime(from && from > first ? from : first, false);
  const end = yeastarDateTime(to && to < last ? to : last, true);

  let inbound: CallStats | null = null;
  let outbound: CallStats | null = null;
  try {
    const [inboundResult, outboundResult] = await Promise.all([
      fetchCallStatsByType([extension.id], start, end, 'Inbound'),
      fetchCallStatsByType([extension.id], start, end, 'Outbound'),
    ]);
    inbound = inboundResult.find((s) => s.ext_num === extension.number) || inboundResult[0] || null;
    outbound = outboundResult.find((s) => s.ext_num === extension.number) || outboundResult[0] || null;
  } catch (error) {
    console.warn(`Failed to fetch stats for ${info.label}:`, error);
  }

  const notAnswered = (stats: CallStats | null) => (stats ? statMissed(stats) : 0);
  const inboundAnswered = inbound?.answered_calls || 0;
  const outboundAnswered = outbound?.answered_calls || 0;
  return {
    month: info.label,
    monthKey: info.key,
    inboundAnswered,
    inboundNoAnswer: notAnswered(inbound),
    inboundTotal: inboundAnswered + notAnswered(inbound),
    outboundAnswered,
    outboundNoAnswer: notAnswered(outbound),
    outboundTotal: outboundAnswered + notAnswered(outbound),
    totalTalkDuration: (inbound?.total_talking_time || 0) + (outbound?.total_talking_time || 0),
  };
}

/** The Call Summary report: Yeastar's extension call statistics per month, the first and last month cut to the dates. */
export async function loadCallSummary(
  extension: Extension,
  startDate: string,
  endDate: string
): Promise<ExtensionReportData> {
  const from = parseDay(startDate);
  const to = parseDay(endDate);
  const months: MonthInfo[] = [];
  for (let current = new Date(from.getFullYear(), from.getMonth(), 1); current <= to; current.setMonth(current.getMonth() + 1)) {
    months.push(monthInfo(current));
  }

  const monthlyData: MonthlyCallData[] = [];
  for (const info of months) monthlyData.push(await monthStats(extension, info, from, to));

  const sum = (pick: (month: MonthlyCallData) => number) => monthlyData.reduce((total, month) => total + pick(month), 0);
  const inboundAnswered = sum((m) => m.inboundAnswered);
  const inboundNoAnswer = sum((m) => m.inboundNoAnswer);
  const outboundAnswered = sum((m) => m.outboundAnswered);
  const outboundNoAnswer = sum((m) => m.outboundNoAnswer);
  const totalTalkDuration = sum((m) => m.totalTalkDuration);
  const answered = inboundAnswered + outboundAnswered;
  return {
    extension,
    period: { startDate, endDate },
    summary: {
      inboundAnswered,
      inboundNoAnswer,
      inboundTotal: inboundAnswered + inboundNoAnswer,
      outboundAnswered,
      outboundNoAnswer,
      outboundTotal: outboundAnswered + outboundNoAnswer,
      totalTalkDuration,
      averageCallDuration: answered > 0 ? Math.round(totalTalkDuration / answered) : 0,
    },
    monthlyData,
  };
}

function isAnsweredRecord(record: CallRecord) {
  const d = record.disposition?.toLowerCase() || '';
  if (d.includes('no answer') || d.includes('noanswer') || d.includes('missed')) return false;
  return d.includes('answer');
}

function isMissedRecord(record: CallRecord) {
  const d = record.disposition?.toLowerCase() || '';
  return (
    d.includes('no answer') ||
    d.includes('noanswer') ||
    d.includes('missed') ||
    d.includes('busy') ||
    d.includes('failed') ||
    d.includes('cancel')
  );
}

/**
 * The Call Detail report: the extension's calls in the period, and a monthly summary of the two
 * months before the end date's month followed by the chosen period itself.
 */
export async function loadCallDetail(
  extension: Extension,
  startDate: string,
  endDate: string
): Promise<{ records: CallRecord[]; monthlyData: MonthlyCallData[] }> {
  const records = await fetchExtensionCDR(
    extension.number,
    `${startDate.replace(/-/g, '/')} 00:00:00`,
    `${endDate.replace(/-/g, '/')} 23:59:59`
  );

  const end = parseDay(endDate);
  const earlier: MonthlyCallData[] = [];
  for (const back of [2, 1]) {
    earlier.push(await monthStats(extension, monthInfo(new Date(end.getFullYear(), end.getMonth() - back, 1))));
  }

  const inbound = records.filter((r) => r.call_type === 'Inbound');
  const outbound = records.filter((r) => r.call_type === 'Outbound');
  const startLabel = parseDay(startDate).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
  const selectedPeriod: MonthlyCallData = {
    month: `${startLabel} - ${endLabel}`,
    monthKey: 'zz-selected',
    inboundAnswered: inbound.filter(isAnsweredRecord).length,
    inboundNoAnswer: inbound.filter(isMissedRecord).length,
    inboundTotal: inbound.length,
    outboundAnswered: outbound.filter(isAnsweredRecord).length,
    outboundNoAnswer: outbound.filter(isMissedRecord).length,
    outboundTotal: outbound.length,
    totalTalkDuration: records.reduce((sum, r) => sum + (r.talk_duration || 0), 0),
  };
  return { records, monthlyData: [...earlier, selectedPeriod] };
}

export function csvLine(cells: Array<string | number | null | undefined>) {
  return cells
    .map((cell) => {
      const value = String(cell ?? '');
      return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    })
    .join(',');
}

/** A CSV file's text: header, rows, Windows line ends and a byte-order mark so Excel reads it as UTF-8. */
export function csvText(header: string[], rows: Array<Array<string | number | null | undefined>>) {
  return `${String.fromCharCode(0xfeff)}${[csvLine(header), ...rows.map(csvLine)].join('\r\n')}`;
}

export function callSummaryCsv(report: ExtensionReportData) {
  const row = (label: string, m: Omit<MonthlyCallData, 'month' | 'monthKey'>) => [
    label,
    m.inboundAnswered,
    m.inboundNoAnswer,
    m.inboundTotal,
    m.outboundAnswered,
    m.outboundNoAnswer,
    m.outboundTotal,
    formatHMS(m.totalTalkDuration),
    m.totalTalkDuration,
  ];
  return csvText(
    ['Month', 'Inbound answered', 'Inbound not answered', 'Inbound total', 'Outbound answered', 'Outbound not answered', 'Outbound total', 'Talk time', 'Talk seconds'],
    [...report.monthlyData.map((month) => row(month.month, month)), row('Total', report.summary)]
  );
}

export function callDetailCsv(records: CallRecord[], formatTime: (record: CallRecord) => string) {
  return csvText(
    ['Date & time', 'Type', 'From', 'To', 'Status', 'Ring seconds', 'Talk time', 'Talk seconds', 'Trunk'],
    records.map((record) => [
      formatTime(record),
      record.call_type,
      record.call_from,
      record.call_to,
      record.disposition || 'Unknown',
      Math.round(record.ring_duration || 0),
      formatHMS(record.talk_duration || 0),
      Math.round(record.talk_duration || 0),
      record.dst_trunk || record.src_trunk || '',
    ])
  );
}
